import React, { useState } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import SuccessModal from './SuccessModal';
import { useTranslation } from 'react-i18next';
import './CreateUserForm.css';
import { 
  Form, 
  Input, 
  Button, 
  DatePicker, 
  Upload, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Divider, 
  message 
} from 'antd';
import { 
  EyeTwoTone, 
  EyeInvisibleOutlined, 
  UploadOutlined,
  KeyOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

const initialFormData = {
  firstname: '',
  lastname: '',
  nickname: '',
  nationalid: '',
  date_of_birth: '',
  email: '',
  password: '',
  role: 'student',
  phone: '',
  address: '',
  currentEducationLevel: '',
  guardianContact: '',
  schoolName: '',
  nationalIdPdf: null,
};

function CreateStudentForm({ onClose }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [formData, setFormData] = useState(initialFormData);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fileList, setFileList] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL;

  const handleChange = (name, value) => {
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    form.setFieldsValue({ password });
    setFormData(prevData => ({
      ...prevData,
      password: password
    }));
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
  
      const dataToSend = new FormData();
      // Properly handle date format for API submission
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'date_of_birth') {
          // Convert moment object to string format
          dataToSend.append(key, value && value.format ? value.format('YYYY-MM-DD') : value);
        } else if (key !== 'nationalIdPdf') {
          dataToSend.append(key, value);
        }
      });
      
      if (fileList.length > 0 && fileList[0].originFileObj) {
        dataToSend.append('nationalIdPdf', fileList[0].originFileObj);
      }
  
      await axios.post(`${API_URL}/users/create`, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        }
      });
  
      message.success('User created successfully!');
      
      // Reset form
      form.resetFields();
      setFormData(initialFormData);
      setFileList([]);
      setShowConfirmModal(false);
  
    } catch (error) {
      if (error.response && error.response.data.message === "Email address is already in use") {
        message.error('Email address is already in use. Please try another email.');
      } else {
        message.error('An error occurred while creating the user. Please try again later.');
      }
    }
  };

  const beforeFormSubmit = (values) => {
    // Update formData with form values, ensuring date is properly formatted
    const updatedData = { 
      ...formData,
      ...values,
      // Convert date to string format if it's a moment object
      date_of_birth: values.date_of_birth ? 
        (values.date_of_birth.format ? values.date_of_birth.format('YYYY-MM-DD') : values.date_of_birth) : '',
    };
    setFormData(updatedData);
    setShowConfirmModal(true);
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    if (e && e.fileList) {
      setFileList(e.fileList);
      if (e.fileList.length > 0) {
        return e.fileList;
      }
    }
    return [];
  };

  return (
    <div className="form-page">
      <Sidebar />
      <Header />
      <div className="form-content">
        <Card className="form-card">
          <Button onClick={() => onClose()} style={{ marginBottom: '16px' }}>
            <ArrowLeftOutlined /> {t('Back')}
          </Button>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
            {t('form.add_new_student')}
          </Title>
          <Form
            form={form}
            layout="vertical"
            onFinish={beforeFormSubmit}
            initialValues={{
              role: 'student',
            }}
          >
            <Divider orientation="left">
              <Text strong>{t('form.student_details')}</Text>
            </Divider>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="firstname"
                  label={<span>{t('form.first_name')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_first_name') }]}
                >
                  <Input 
                    placeholder={t('form.first_name')} 
                    onChange={(e) => handleChange('firstname', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="lastname"
                  label={<span>{t('form.last_name')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_last_name') }]}
                >
                  <Input 
                    placeholder={t('form.last_name')} 
                    onChange={(e) => handleChange('lastname', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="nickname"
                  label={<span>{t('form.nickname')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_nickname') }]}
                >
                  <Input 
                    placeholder={t('form.nickname')} 
                    onChange={(e) => handleChange('nickname', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="nationalid"
                  label={<span>{t('form.national_id')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_national_id') }]}
                >
                  <Input 
                    placeholder={t('form.national_id')} 
                    onChange={(e) => handleChange('nationalid', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="date_of_birth"
                  label={<span>{t('form.date_of_birth')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_date_of_birth') }]}
                >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    onChange={(date) => handleChange('date_of_birth', date)}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="currentEducationLevel"
                  label={<span>{t('form.current_education_level')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_education_level') }]}
                >
                  <Input 
                    placeholder={t('form.current_education_level')} 
                    onChange={(e) => handleChange('currentEducationLevel', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="email"
                  label={<span>{t('form.email')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[
                    { required: true, message: t('form.please_enter_email') },
                    { type: 'email', message: t('form.please_enter_valid_email') }
                  ]}
                >
                  <Input 
                    placeholder={t('form.email')} 
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="phone"
                  label={<span>{t('form.phone')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_phone') }]}
                >
                  <Input 
                    placeholder={t('form.phone')} 
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="guardianContact"
                  label={<span>{t('form.parent_phone')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_parent_phone') }]}
                >
                  <Input 
                    placeholder={t('form.parent_phone')} 
                    onChange={(e) => handleChange('guardianContact', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="password"
                  label={<span>{t('form.temporary_password')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_password') }]}
                >
                  <Input.Password
                    placeholder={t('form.temporary_password')}
                    onChange={(e) => handleChange('password', e.target.value)}
                    iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    addonAfter={
                      <Button 
                        type="text" 
                        icon={<KeyOutlined />} 
                        onClick={generateRandomPassword}
                        style={{ marginRight: -8 }}
                      />
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="address"
                  label={<span>{t('form.address')} <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: t('form.please_enter_address') }]}
                >
                  <TextArea 
                    rows={4} 
                    placeholder={t('form.address')} 
                    onChange={(e) => handleChange('address', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="nationalIdPdf"
                  label={<span>{t('form.national_id_pdf')} <span style={{ color: 'red' }}>*</span></span>}
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  rules={[{ required: false, message: t('form.please_upload_national_id') }]}
                >
                  <Upload 
                    name="nationalIdPdf" 
                    accept=".pdf"
                    beforeUpload={() => false}
                    maxCount={1}
                    fileList={fileList}
                  >
                    <Button icon={<UploadOutlined />}>{t('form.upload_pdf')}</Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item>
              <Button type="primary" htmlType="submit" block style={{ height: '40px', marginTop: '16px' }}>
                {t('form.create_student')}
              </Button>
            </Form.Item>
          </Form>
        </Card>
        
        {showConfirmModal && (
          <SuccessModal
            details={formData}
            onClose={() => setShowConfirmModal(false)}
            onConfirm={handleSubmit}
          />
        )}
      </div>
    </div>
  );
}

export default withRoleAccess(CreateStudentForm, ['admin', 'superadmin']);